/**
 * PerkDef — definition and instance of a player perk.
 *
 * Perks are either binary (have it or don't) or stackable.
 * Like skills, they register modifiers/hooks into the ModifierBag
 * at battle start via BattleBagBuilder.
 */

import type { ModifierBag } from "../../../framework/stats";

/** How a perk registers its effects into the battle modifier bag. */
export type PerkApplyFn = (bag: ModifierBag, stacks: number) => void;

/**
 * Static perk definition — lives in the catalog.
 */
export interface PerkDef {
  /** Unique identifier. */
  id: string;
  /** Display name. */
  name: string;
  /** Base description. */
  description: string;
  /** Badge color for UI. */
  color: string;
  /** Icon character for UI badge. */
  icon: string;
  /** Whether this perk can be taken multiple times (default false = binary). */
  stackable: boolean;
  /** Max stacks if stackable (ignored if not stackable). */
  maxStacks: number;
  /** Selection weight — higher = more likely to appear in choices. */
  weight: number;
  /**
   * Register modifiers and hooks into the battle bag.
   * Called by BattleBagBuilder at battle start.
   */
  apply: PerkApplyFn | null;
  /**
   * Get a named out-of-combat value for this perk.
   * Returns 0 if no such value exists.
   */
  getValue?: (stacks: number, key: string) => number;
  /**
   * Dynamic description showing current stack info.
   */
  descFn?: (stacks: number) => string;
}

/**
 * PerkInstance — a perk the player has acquired.
 */
export class PerkInstance {
  readonly def: PerkDef;
  private _stacks: number;

  constructor(def: PerkDef, stacks: number = 1) {
    this.def = def;
    this._stacks = stacks;
  }

  get id(): string { return this.def.id; }
  get name(): string { return this.def.name; }
  get stacks(): number { return this._stacks; }
  get canStack(): boolean {
    if (!this.def.stackable) return false;
    return this._stacks < this.def.maxStacks;
  }

  /** Add a stack (for stackable perks). */
  addStack(): void {
    if (this.def.stackable && this._stacks < this.def.maxStacks) {
      this._stacks++;
    }
  }

  /** Apply this perk's modifiers/hooks to a battle bag. */
  apply(bag: ModifierBag): void {
    this.def.apply?.(bag, this._stacks);
  }

  /** Get an out-of-combat value. */
  getValue(key: string): number {
    return this.def.getValue?.(this._stacks, key) ?? 0;
  }

  /** Get description for current stacks. */
  get description(): string {
    return this.def.descFn?.(this._stacks) ?? this.def.description;
  }
}
